/**
 * PlayerModel — Factory for player avatars on the 3D field.
 *
 * Tier C (current): each player is a full skinned humanoid mesh (Mixamo
 * Vanguard derivative) with procedural football helmet + shoulder pads
 * parented to the head and chest bones. Per-team color tinting on the body
 * uniform and helmet shell distinguishes offense / defense / QB.
 *
 * The ball is a procedural prolate spheroid.
 *
 * createPlayer() is async because it awaits the shared humanoid GLB load
 * (cached after first call). FootballScene3D awaits all createPlayer promises
 * before flagging the scene ready.
 *
 * To swap in a higher-fidelity character, replace public/three/humanoid_base.glb
 * with any Mixamo-rigged GLB; the loader will pick it up automatically.
 */

import * as THREE from 'three';
import { instantiateHumanoid, archetypeForRole } from './HumanoidLoader';
import {
  createHelmet,
  createShoulderPads,
  createFootball,
  tintHumanoidUniform,
  resolveUniformKit,
} from './FootballGear';
import {
  resolveRole,
  resolveMesh,
  resolveVerb,
  resolveIntensity,
  pickLocomotionClip,
  DEFAULT_INTENSITY,
} from './AssetManifest';
import { loadClip, loadClipForMesh, loadClipForVerb, preloadClips } from './ClipLoader';

function parseHex(c) {
  if (typeof c === 'number') return c;
  if (!c) return 0xffffff;
  return parseInt(String(c).replace('#', '').replace(/^0x/i, ''), 16) || 0xffffff;
}

/**
 * True when an [x, y, z] offset vector is exactly [0,0,0]. Used as a
 * manifest "skip me" flag for procedural helmet / shoulder-pads attach:
 * a mesh that ships its own sculpted gear (e.g. Studio Ochi) declares
 * `helmet_offset: [0,0,0]` and `pads_offset: [0,0,0]` so PlayerModel
 * doesn't stack a procedural helmet on top of the authored one.
 *
 * Defensive about shape: returns false for any non-array or non-3-tuple
 * so a missing manifest field doesn't accidentally suppress gear on
 * legacy meshes (xbot/vanguard) that genuinely need it.
 */
function _isAllZero(vec) {
  if (!Array.isArray(vec) || vec.length !== 3) return false;
  return vec[0] === 0 && vec[1] === 0 && vec[2] === 0;
}

// ─── Per-team Ochi jersey tinting ────────────────────────────────────────────

/**
 * Apply a coach-authored team kit color to a Studio Ochi player.
 *
 * Background — see `docs/OCHI_MATERIALS.md`:
 *   * Each Ochi GLB ships ONE SkinnedMesh, ONE primitive, ONE material
 *     ("Athletes 04/05/06"), ONE baked PBR texture covering the whole
 *     body (skin / jersey / pads / helmet / cleats — all in the same
 *     UV atlas). There's no per-region material slot to tint
 *     individually, so this is the manifest's "Branch B" path: tint
 *     the whole material.
 *   * Ochi's baked texture is strongly saturated (Man A is red, Man C
 *     is green/yellow). A naïve `material.color = teamPrimary` multiply
 *     in shader runs into the Branch-C failure mode — red painted ×
 *     blue requested = near-black, blue painted × red requested =
 *     brown. Vivid team colors require neutralising the bake first.
 *
 * Implementation:
 *
 *   1. Walk the player's group to find SkinnedMesh nodes (Ochi has
 *      exactly one, but we tolerate multi-primitive future exports).
 *   2. Clone every material so per-actor tints don't bleed across the
 *      shared GLTF cache. (This is the bug the task brief calls out
 *      explicitly — `GLTFLoader` returns the same material instance
 *      to every consumer of the cached template; `SkeletonUtils.clone`
 *      does NOT clone materials.)
 *   3. Inject an `onBeforeCompile` patch on each cloned material that
 *      REPLACES the diffuse texel chroma with a renormalised luminance
 *      curve BEFORE Three's stock `<map_fragment>` multiplies by
 *      `diffuse` (== `material.color`). The curve
 *
 *          boosted = pow( lum, uLumGamma ) * uLumScale + uLumBias
 *
 *      lifts Ochi's saturated atlas pixels (typical luminance 0.10–
 *      0.40 once Rec. 709 weights collapse the dominant red/green
 *      channels) into the 0.55–0.95 range. The subsequent multiply
 *      by `material.color = teamPrimary` then produces a vivid team-
 *      tinted result instead of the near-black blobs we got from the
 *      previous desaturate-then-multiply path (commit b0539bb1).
 *      Texture detail (numbers, stripes, fabric folds, normal-map
 *      shading) survives untouched because it lives entirely in the
 *      brightness channel and the curve is monotonic.
 *   4. Set `material.color` to the team primary. Three's stock
 *      `diffuseColor *= sampledDiffuseColor;` multiply then runs
 *      with our boosted-grayscale texel against the team primary →
 *      vivid, readable team-coloured player.
 *
 * Why this isn't just `material.color * texture`:
 *   The first attempt (commit b0539bb1) used `mix(rgb, vec3(lum),
 *   0.85)` to desaturate, then multiplied by team color. Histograms
 *   confirmed offense / defense resolved to distinct hues — but
 *   visually players read as near-black blobs against the green
 *   field. Root cause: the painted Ochi atlas is *dark* in luminance
 *   (saturated red has Rec. 709 luminance ≈ 0.32), and pure multiply
 *   against navy (≈ 0.23 in green channel) drives the result to
 *   ≈ 0.07. This pipeline lifts brightness *before* the multiply —
 *   the standard NCAA / Madden / EA Sports team-rules technique.
 *
 * Three's `MeshStandardMaterial` uses the `vMapUv` varying for the
 * baseColorTexture sample (Three r150+, confirmed against r0.183 in
 * use here — `node_modules/three/src/renderers/shaders/ShaderChunk/
 * map_fragment.glsl.js`). The three uniforms below are stored on
 * `material.userData.<name>.value` so a future art-direction pass can
 * tune (`material.userData.uLumGamma.value = 0.35`) at runtime
 * without rebuilding the bundle.
 *
 * @param {THREE.Object3D} humanoidRoot — the Ochi player root from instantiateHumanoid().root
 * @param {object} kit                  — kit dict from resolveUniformKit:
 *                                          { jersey: hexInt, accent, helmet, pads, facemask }
 * @param {object} [opts]               — { lumGamma?, lumScale?, lumBias? }
 *                                          Defaults: 0.45, 0.95, 0.05
 */
export function tintOchiUniform(humanoidRoot, kit, opts = {}) {
  if (!humanoidRoot || !kit) return;
  const lumGamma = (typeof opts.lumGamma === 'number') ? opts.lumGamma : 0.45;
  const lumScale = (typeof opts.lumScale === 'number') ? opts.lumScale : 0.95;
  const lumBias  = (typeof opts.lumBias  === 'number') ? opts.lumBias  : 0.05;
  const tintHex = (typeof kit.jersey === 'number') ? kit.jersey : parseHex(kit.jersey);

  humanoidRoot.traverse((obj) => {
    if (!obj.isSkinnedMesh) return;
    const srcMats = Array.isArray(obj.material) ? obj.material : [obj.material];
    const tintedMats = srcMats.map((mat) => {
      if (!mat) return mat;
      // CRITICAL — clone before mutating. GLTFLoader caches material
      // instances and SkeletonUtils.clone() reuses them across player
      // instances; mutating in place would mean home-team color shows
      // up on every away-team player too (last write wins on the
      // shared instance).
      const cloned = mat.clone();
      cloned.color = new THREE.Color(tintHex);
      cloned.needsUpdate = true;
      _installOchiTeamColorPatch(cloned, { lumGamma, lumScale, lumBias });
      return cloned;
    });
    obj.material = Array.isArray(obj.material) ? tintedMats : tintedMats[0];
  });
}

/**
 * Compile-time shader patch that:
 *
 *   1. Samples the painted Ochi diffuse texel.
 *   2. Computes Rec. 709 perceptual luminance.
 *   3. Renormalises that luminance via `pow(lum, uLumGamma) *
 *      uLumScale + uLumBias` so dark fabric pixels (typical Ochi
 *      atlas luminance 0.10–0.40) map into a useful brightness range
 *      (0.55–0.95) where the subsequent team-color multiply produces
 *      a vivid result rather than near-black.
 *   4. For NON-skin pixels (jersey, helmet, pads, cleats), throws
 *      away ALL chroma — the texel becomes pure renormalised
 *      grayscale before Three's stock `diffuseColor *=
 *      sampledDiffuseColor;` multiply runs. Team color then *replaces*
 *      the baked color while jersey numbers / stripes / fabric folds /
 *      normal-map shading modulate it.
 *   5. For SKIN pixels (face, hands, forearms — classified by HSV
 *      hue 18°-50° / sat 0.20-0.65 / val 0.30-0.95) the original
 *      painted RGB is preserved by counter-dividing the team color
 *      out, so the downstream multiply lands the natural skin tone.
 *      Coaches reported "uniforms applied to the whole body — players
 *      look like robots" before this branch existed.
 *
 * Tuning knobs (all on `material.userData.<name>.value`,
 * runtime-tunable without a rebuild):
 *
 *   uLumGamma  — 0.45 default. Lower = lift shadows harder (brighter
 *                players, less detail in dark areas). Drop to 0.35 if
 *                players still read too dark; bump to 0.55 if
 *                highlights blow out and texture detail disappears.
 *   uLumScale  — 0.95 default. Linear gain on the post-gamma value.
 *                Drop to 0.85 for a more muted look.
 *   uLumBias   — 0.05 default. Floor — keeps deep-shadow pixels from
 *                going to absolute zero (which would crush the texture
 *                detail we worked to preserve).
 */
function _installOchiTeamColorPatch(material, knobs) {
  material.userData = material.userData || {};
  material.userData.uLumGamma = { value: knobs.lumGamma };
  material.userData.uLumScale = { value: knobs.lumScale };
  material.userData.uLumBias  = { value: knobs.lumBias };
  // Tag so we can identify Ochi-tinted materials in dev tools / tests.
  material.userData.isOchiTinted = true;

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uLumGamma = material.userData.uLumGamma;
    shader.uniforms.uLumScale = material.userData.uLumScale;
    shader.uniforms.uLumBias  = material.userData.uLumBias;

    shader.fragmentShader =
      'uniform float uLumGamma;\nuniform float uLumScale;\nuniform float uLumBias;\n'
      + shader.fragmentShader;

    // Skin-tone preservation:
    //
    // The luminance-replace + team-color multiply produces vivid jersey
    // colors but, applied indiscriminately, also tints the player's
    // face, hands, and forearms — coaches reported "uniforms applied
    // to the whole body, players look like robots." Ochi's painted
    // atlas packs skin alongside jersey in the same UV space; we can't
    // mask it from outside the shader, so we discriminate in the
    // fragment by classifying the sampled texel as skin or non-skin.
    //
    // Skin range (validated against the ochi_man_a/b/c painted faces
    // and forearms via the `scripts/ochi_inspect_glb.py` color dump):
    //   hue 18°..50°  (warm orange-yellow band — covers the lightest
    //                  Man C face down to the deepest Man B cheek
    //                  shadow)
    //   sat 0.20..0.65
    //   val 0.30..0.95
    //
    // Pixels classified as skin BYPASS the luminance renormalize +
    // team-color multiply: we counter-divide by the diffuse color so
    // the stock `diffuseColor *= sampledColor` multiply downstream
    // lands the original painted skin RGB on screen unchanged. Stock
    // Three doesn't expose a way to skip the multiply conditionally,
    // hence the divide trick.
    //
    // Edge case: if any team primary channel is below ~0.05, dividing
    // by it amplifies skin RGB beyond 1.0 (clamped by the framebuffer
    // but produces a visible bloom). The `max(teamColor, vec3(0.05))`
    // clamp keeps the skin region bounded for any plausible team
    // palette.
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      [
        '#ifdef USE_MAP',
        '  vec4 sampledTexel = texture2D( map, vMapUv );',
        '  vec3 c = sampledTexel.rgb;',
        // Compact RGB→HSV. Hue in degrees [0,360), sat/val in [0,1].
        '  float cmax = max(c.r, max(c.g, c.b));',
        '  float cmin = min(c.r, min(c.g, c.b));',
        '  float delta = cmax - cmin;',
        '  float h = 0.0;',
        '  if (delta > 0.0001) {',
        '    if (cmax == c.r) h = mod((c.g - c.b) / delta, 6.0);',
        '    else if (cmax == c.g) h = (c.b - c.r) / delta + 2.0;',
        '    else h = (c.r - c.g) / delta + 4.0;',
        '    h = h * 60.0; if (h < 0.0) h += 360.0;',
        '  }',
        '  float s = (cmax > 0.0001) ? delta / cmax : 0.0;',
        '  float v = cmax;',
        // Skin classifier. step() chain returns 1.0 when ALL conditions
        // hold, 0.0 otherwise → branchless mix() select.
        '  float isSkin = step(18.0, h) * step(h, 50.0)',
        '               * step(0.20, s) * step(s, 0.65)',
        '               * step(0.30, v) * step(v, 0.95);',
        // Non-skin branch: the original luminance-replace + team-color
        // multiply (vivid jersey color regardless of the painted hue).
        '  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));',
        '  float boosted = pow(clamp(lum, 0.0, 1.0), uLumGamma) * uLumScale + uLumBias;',
        '  vec3 jerseyMul = vec3(boosted);',
        // Skin branch: counter-divide by team color so the downstream
        // `diffuseColor *= sampledColor` lands the original painted RGB.
        '  vec3 teamColor = max(diffuseColor.rgb, vec3(0.05));',
        '  vec3 skinMul = c / teamColor;',
        '  vec3 finalMul = mix(jerseyMul, skinMul, isSkin);',
        '  diffuseColor.rgb *= finalMul;',
        '  diffuseColor.a   *= sampledTexel.a;',
        '#endif',
      ].join('\n'),
    );
  };
}

/**
 * Sprint 3 — movement_type → animation clip mapping.
 *
 * Each entry maps a backend MovementType (see core/scenario/dsl/schema.py)
 * to a list of clip-name candidates. We try each candidate against the
 * loaded GLB's clip registry in order; the first match wins. Falls back
 * to Run / Walk / Idle so legacy scenes (no movement_type) still play
 * correctly even when none of the new clips ship on the GLB.
 *
 * Authors of higher-fidelity Tier-D GLBs can add more specific clips
 * (e.g. "Backpedal", "Shuffle_L", "Pass_Drop") and this registry will
 * pick them up automatically without code changes.
 */
const MOVEMENT_TYPE_TO_CLIP = {
  idle:           ['Idle'],
  walk:           ['Walk'],
  jog:            ['Jog', 'Run'],
  run:            ['Run', 'Jog'],
  sprint:         ['Sprint', 'Run', 'Jog'],
  backpedal:      ['Backpedal', 'Walk'],
  shuffle_left:   ['Shuffle_L', 'Shuffle', 'Walk'],
  shuffle_right:  ['Shuffle_R', 'Shuffle', 'Walk'],
  pass_drop:      ['Pass_Drop', 'Backpedal', 'Walk'],
  pass_block:     ['Pass_Block', 'Idle'],
  drive_block:    ['Drive_Block', 'Run', 'Walk'],
  press_release:  ['Press_Release', 'Run'],
  break:          ['Break', 'Run'],
};

/** Resolve a movement_type to an Action available on this player. */
export function actionForMovementType(group, movementType) {
  const actions = group?.userData?.actions || {};
  const candidates = MOVEMENT_TYPE_TO_CLIP[movementType] || [];
  for (const clipName of candidates) {
    if (actions[clipName]) return actions[clipName];
  }
  return null;
}

// ─── Sprint 5 — Verb-driven animation pipeline (Layer 3) ─────────────────────

/**
 * Lazy-create an AnimationAction for a manifest clip id on this player.
 *
 * Resolution strategy (in order):
 *
 *   1. **Embedded native clip via alias** — when the player's mesh
 *      manifest declares a `native_clip_aliases` map (Studio Ochi)
 *      and an alias is set for this verb's clip id, we look for an
 *      embedded AnimationClip in the mesh GLB whose name CONTAINS
 *      the alias substring. Hit → wrap in mixer.clipAction and cache.
 *      No network round-trip, no retargeting cost, native artist
 *      animation quality.
 *
 *   2. **ClipLoader Mixamo (with optional retarget)** — falls back
 *      to loadClipForMesh, which fetches the per-verb anim_*.glb,
 *      runs THREE.SkeletonUtils.retargetClip when the mesh declares
 *      a `bone_name_remap`, and caches the result.
 *
 * Per-player AnimationAction cache lives on group.userData.manifestActions
 * so subsequent calls with the same clipId return the cached Action
 * immediately — safe to call from the per-frame update loop.
 */
export async function ensureClipAction(group, clipId) {
  if (!group || !group.userData || !group.userData.mixer) return null;
  const cache = group.userData.manifestActions;
  if (cache[clipId]) return cache[clipId];
  const meshId = group.userData.meshId;

  // Path 1 — try the mesh's native embedded clip via alias.
  const nativeAction = _ensureNativeClipAction(group, clipId);
  if (nativeAction) {
    cache[clipId] = nativeAction;
    return nativeAction;
  }

  // Path 2 — Mixamo (with retarget when bone_name_remap is set).
  let clip;
  try {
    clip = await loadClipForMesh(clipId, meshId);
  } catch (err) {
    console.warn(`PlayerModel.ensureClipAction: failed to load clip "${clipId}" for mesh "${meshId}"`, err && err.message);
    return null;
  }
  const action = group.userData.mixer.clipAction(clip);
  cache[clipId] = action;
  return action;
}

/**
 * Try to resolve a manifest clip id to one of the AnimationClips
 * that shipped embedded in the mesh GLB. Returns the
 * AnimationAction (cached on the mixer) or null when no alias
 * matches.
 *
 * Uses substring match because Blender 4.2's glTF exporter prefixes
 * action names with the source armature's name. e.g. the "Hold"
 * action on Ochi's Man A exports as "Metarig Man.013|Metarig
 * Man.013|American Football Hold" — alias = 'Hold' matches that
 * via String.includes.
 *
 * Alias values may be a string substring OR an object
 * `{ clip, loop?, time?, paused? }` — see AssetManifest.OCHI_NATIVE_CLIP_ALIASES.
 * When an alias is an object, the extracted opts are stashed on
 * `action._aliasOpts` so PlayerModel.applyAliasOpts can apply
 * them after every action.play() call (resetting time/paused
 * which AnimationAction.reset() would otherwise clear).
 */
function _ensureNativeClipAction(group, clipId) {
  const meshId = group.userData?.meshId;
  if (!meshId) return null;
  const meshDef = resolveMesh(meshId);
  const aliases = meshDef && meshDef.native_clip_aliases;
  if (!aliases) return null;
  const aliasValue = aliases[clipId];
  if (!aliasValue) return null;

  let needle;
  let aliasOpts = null;
  if (typeof aliasValue === 'string') {
    needle = aliasValue;
  } else if (typeof aliasValue === 'object') {
    needle = aliasValue.clip;
    aliasOpts = {
      loop:   aliasValue.loop !== undefined ? aliasValue.loop : true,
      time:   typeof aliasValue.time === 'number' ? aliasValue.time : null,
      paused: aliasValue.paused === true,
    };
  }
  if (!needle) return null;

  const embedded = group.userData?.actions || {};
  for (const name of Object.keys(embedded)) {
    if (name && name.indexOf(needle) !== -1) {
      const action = embedded[name];
      if (aliasOpts) {
        action._aliasOpts = aliasOpts;
      }
      return action;
    }
  }
  return null;
}

/**
 * Apply a cached alias-opts blob to an AnimationAction. Called after
 * action.play() / action.reset() to (re)assert frozen-frame poses
 * authored in OCHI_NATIVE_CLIP_ALIASES.
 *
 * AnimationAction.reset() clears `time`, `paused`, and `enabled`, so
 * we MUST call this after every reset/play if we want a "freeze at
 * t=0" alias to actually freeze. mixer.update(0) is also needed to
 * push the new time onto the bones; otherwise the action sits at
 * t=0 internally but the skeleton stays in whatever pose the prior
 * mixer step left it at.
 */
function applyAliasOpts(group, action) {
  const opts = action && action._aliasOpts;
  if (!opts) return;
  if (typeof opts.time === 'number') action.time = opts.time;
  if (opts.paused) action.paused = true;
  // Push the freeze pose into the bones immediately. Cheap; the mixer
  // would do this on the next frame anyway, but doing it here means
  // the FIRST rendered frame after the stance mounts already shows
  // the correct upright / QB pose instead of the bind pose.
  if (group.userData && group.userData.mixer) {
    group.userData.mixer.update(0);
  }
}

/**
 * Crossfade the player to a different manifest clip.
 *
 * @param {THREE.Group} group       — the player group from createPlayer
 * @param {string} clipId           — manifest clip id (must be in CLIPS)
 * @param {object} opts             — { loop, time_scale, fade_ms, one_shot_ms }
 *                                    All optional; defaults come from the manifest.
 * @returns {Promise<THREE.AnimationAction|null>}
 */
export async function playManifestClip(group, clipId, opts = {}) {
  const action = await ensureClipAction(group, clipId);
  if (!action) return null;
  const fadeMs = opts.fade_ms != null ? opts.fade_ms : 250;
  const loop = opts.loop !== undefined ? opts.loop : true;
  const timeScale = opts.time_scale || 1.0;

  action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
  action.clampWhenFinished = !loop;
  action.timeScale = timeScale;
  action.enabled = true;

  const prev = group.userData.activeAction;
  if (prev && prev !== action) {
    action.reset();
    action.crossFadeFrom(prev, fadeMs / 1000, false);
    action.play();
  } else if (!prev) {
    action.reset();
    action.play();
  }
  // Re-apply any frozen-frame opts (time/paused) from the native
  // clip alias. action.reset() clears these every time, so we have
  // to assert them after play(). See OCHI_NATIVE_CLIP_ALIASES doc.
  applyAliasOpts(group, action);
  group.userData.activeAction = action;
  group.userData.activeClipId = clipId;

  // For one-shot verbs, schedule a crossfade back to the role's default
  // idle when the clip finishes. We cancel any prior pending revert so
  // a rapid sequence (hike → pass → catch) doesn't get confused.
  if (group.userData.revertTimer) {
    clearTimeout(group.userData.revertTimer);
    group.userData.revertTimer = null;
  }
  if (opts.one_shot_ms && opts.one_shot_ms > 0) {
    group.userData.revertTimer = setTimeout(() => {
      const role = group.userData.roleManifest;
      if (role && role.default_idle) {
        playManifestClip(group, role.default_idle, { loop: true, fade_ms: 200 })
          .catch(() => {});
      }
    }, opts.one_shot_ms);
  }
  return action;
}

/**
 * Top-level coach-facing: apply an `assignment` verb (and optional
 * `intensity`) to a player. This is what FootballScene3D calls each
 * keyframe transition.
 *
 *   assignment = "pass_block" → manifest VERBS.pass_block.clip = "football_stance"
 *                              → playManifestClip(group, "football_stance", { loop: true })
 *
 *   assignment = "hike" → one-shot, reverts to role.default_idle after 600ms
 *
 * When `assignment` is null/unknown:
 *   - If the player is moving (speed > 0.1), pick a locomotion clip
 *     from the manifest based on facing + intensity.
 *   - Otherwise, fall back to the role's pre_snap_stance / default_idle.
 *
 * Returns a Promise that resolves once the clip is mounted.
 */
export async function applyAssignment(group, assignment, intensity, opts = {}) {
  if (!group || !group.userData) return null;
  // 1. Coach explicitly authored a verb — honour it.
  if (assignment) {
    const verb = resolveVerb(assignment);
    if (verb && verb.clip) {
      const intensityCfg = resolveIntensity(intensity);
      return playManifestClip(group, verb.clip, {
        loop: verb.loop !== false,
        time_scale: verb.time_scale || intensityCfg.time_scale || 1.0,
        one_shot_ms: verb.one_shot_ms,
        fade_ms: opts.fade_ms,
      });
    }
    console.warn(`PlayerModel.applyAssignment: unknown verb "${assignment}", falling back to role default`);
  }
  // 2. No verb but player is moving — locomotion picker.
  const speed = opts.speed || 0;
  if (speed > 0.1 && opts.velocity) {
    const clipId = pickLocomotionClip({
      velocity: opts.velocity,
      facingY: group.rotation.y,
      intensity,
    });
    if (clipId) {
      const intensityCfg = resolveIntensity(intensity);
      return playManifestClip(group, clipId, {
        loop: true,
        time_scale: intensityCfg.time_scale || 1.0,
        fade_ms: opts.fade_ms,
      });
    }
  }
  // 3. Stationary, no verb — pre-snap stance.
  const role = group.userData.roleManifest;
  const idleClip = role && (role.pre_snap_stance || role.default_idle);
  if (idleClip) {
    return playManifestClip(group, idleClip, { loop: true, fade_ms: opts.fade_ms });
  }
  return null;
}

/**
 * Render a jersey number badge canvas. Mounts as a sprite on the
 * player's chest so cameras at any distance can read it. Optional —
 * skipped when actor has no `jersey_number` set.
 */
function makeJerseyNumberSprite(jerseyNumber, fontColor) {
  const text = String(jerseyNumber);
  const canvas = document.createElement('canvas');
  const size = 96;
  canvas.width = 192;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `900 ${size}px "Arial Black", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 6;
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
  ctx.fillStyle = fontColor || '#ffffff';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(0.5, 0.34, 1);
  sprite.position.set(0, 1.30, 0.20);
  sprite.userData.isJerseyNumber = true;
  return sprite;
}

function makeLabelSprite(text, color) {
  const canvas = document.createElement('canvas');
  const size = 48;
  canvas.width = size * Math.max(text.length, 2) * 0.7 + 24;
  canvas.height = size + 16;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.beginPath();
  ctx.roundRect(0, 0, canvas.width, canvas.height, 6);
  ctx.fill();

  ctx.font = `bold ${size}px Arial`;
  ctx.fillStyle = color || '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  const aspect = canvas.width / canvas.height;
  const h = 1.6;
  sprite.scale.set(h * aspect, h, 1);
  sprite.name = 'label';
  return sprite;
}

function _highlightForRole(role) {
  if (role === 'quarterback') return 0xffd84a;
  return null;
}

/**
 * @param actor          DSL Actor dict (actor_id, role, team, label, jersey_number?)
 * @param entityVisual   manifest visual config (color, etc.)
 * @param uniformSpec    Sprint 2 UniformSpec from Scene.uniform_overrides[team]
 *                       (or null/undefined for legacy single-color behaviour).
 */
export async function createPlayer(actor, entityVisual, uniformSpec = null) {
  const group = new THREE.Group();
  group.name = actor.actor_id;
  group.userData = {
    actorId: actor.actor_id,
    role: actor.role,
    team: actor.team,
    mixer: null,
    activeAction: null,
    actions: {},
  };

  const teamColor = parseHex(entityVisual?.color);
  const isBall = actor.team === 'ball';

  if (isBall) {
    const ball = createFootball();
    ball.position.y = 0.5;
    group.add(ball);
    return group;
  }

  // Resolve uniform spec → 4-color kit. When uniformSpec is null we
  // fall back to the manifest team color in every slot, preserving
  // legacy behaviour for scenes that don't carry uniform_overrides.
  const fallbackHex = `#${teamColor.toString(16).padStart(6, '0')}`;
  const kit = resolveUniformKit(uniformSpec, {
    primary: fallbackHex,
    helmet: fallbackHex,
    pads: fallbackHex,
    accent: fallbackHex,
  });
  group.userData.uniformKit = kit;

  // Sprint 5 — Layer-2 (AssetManifest) routes the role to a mesh id.
  // The renderer (this code) just asks the manifest "what mesh for this
  // role?" and instantiates whatever it returns. Today every role maps
  // to xbot; tomorrow when the Synty pack is dropped in, the manifest
  // can map OL/DL to a chunky mesh and skill to a slim mesh without any
  // change here.
  const roleManifest = resolveRole(actor.role);
  const meshId = roleManifest.mesh; // "xbot" or whatever the manifest says

  let humanoid;
  try {
    humanoid = await instantiateHumanoid(meshId);
  } catch (err) {
    console.error('PlayerModel: humanoid load failed, falling back to disc', err);
    return _fallbackDisc(group, actor, kit.jersey);
  }

  const { root, mixer, clips, headBone, chestBone, resolvedArchetype } = humanoid;
  group.userData.meshId = meshId;
  group.userData.archetype = humanoid.archetype;
  group.userData.resolvedArchetype = resolvedArchetype;
  group.userData.roleManifest = roleManifest;

  // Inspect the resolved manifest entry — meshes that ship their own
  // sculpted football gear (Studio Ochi) declare zero helmet/pads
  // offsets so PlayerModel suppresses the procedural attach pass
  // (we don't want a procedural helmet stacked on Ochi's authored
  // one). Per-team jersey tinting still happens for Ochi via the
  // dedicated `tintOchiUniform` path — see below.
  const resolvedMeshId = humanoid.resolvedArchetype || meshId;
  const meshDef = resolveMesh(resolvedMeshId);
  const skipProceduralGear = meshDef && _isAllZero(meshDef.helmet_offset)
    && _isAllZero(meshDef.pads_offset);
  const isOchiMesh = typeof resolvedMeshId === 'string'
    && resolvedMeshId.indexOf('ochi_') === 0;

  // Per-team uniform tint — branch on mesh family.
  //
  //   * Ochi meshes ship one combined material per character (skin +
  //     jersey + helmet baked into the same atlas) so we can't pick out
  //     a jersey slot to tint in isolation. `tintOchiUniform` clones
  //     the material and runs an `onBeforeCompile` shader patch that
  //     desaturates the painted texture before the team-color multiply
  //     — vivid team colors regardless of which painted variant the
  //     character ships with, jersey detail (numbers, stripes,
  //     "PLAYER" lettering) survives untouched. See
  //     `docs/OCHI_MATERIALS.md` for the inspection that drove this
  //     choice. Without this branch the coach `home/away`
  //     `uniform_overrides` propagated through every layer of the
  //     stack and then died at the renderer because Ochi's painted
  //     texture won the visible-pixel argument.
  //
  //   * X-Bot / Vanguard / any other non-Ochi mesh keeps the legacy
  //     `tintHumanoidUniform` path — honours `uniformMaskMaterial` if
  //     declared (Tier D), falls back to whole-mesh flat tint
  //     (Tier C). That codepath is unchanged.
  if (isOchiMesh) {
    tintOchiUniform(root, kit);
  } else if (!skipProceduralGear) {
    // tintHumanoidUniform honors a userData.uniformMaskMaterial slot on
    // the SkinnedMesh when the Tier D asset declares one — only the jersey
    // material is recolored. Tier C / fallback assets get the whole-mesh
    // tint behaviour they had before.
    tintHumanoidUniform(root, kit.jersey, 0x222222);
  }

  group.add(root);

  if (!skipProceduralGear) {
    // QB stays gold-helmeted — coaches expect the QB to be visually
    // distinct in install diagrams. Unless an explicit uniform_overrides
    // sets a helmet, we reserve this highlight for the QB role.
    const roleHighlight = _highlightForRole(actor.role);
    const helmetShell = (uniformSpec && (uniformSpec.helmet || uniformSpec.primary))
      ? kit.helmet
      : (roleHighlight !== null ? roleHighlight : kit.helmet);

    const helmet = createHelmet({
      shellColor: helmetShell,
      faceMaskColor: kit.facemask,
    });
    if (headBone) {
      headBone.add(helmet);
      helmet.position.set(0, 0.06, 0.02);
      helmet.rotation.set(0, 0, 0);
    } else {
      helmet.position.set(0, 1.78, 0);
      group.add(helmet);
    }

    const pads = createShoulderPads({
      padColor: kit.pads,
      accentColor: kit.accent !== kit.pads ? kit.accent : null,
    });
    if (chestBone) {
      chestBone.add(pads);
      pads.position.set(0, 0.12, 0.0);
    } else {
      pads.position.set(0, 1.45, 0);
      group.add(pads);
    }
  }

  // Optional jersey number badge — skipped when actor has none.
  if (actor.jersey_number !== null && actor.jersey_number !== undefined) {
    const fontColor = (uniformSpec && uniformSpec.jersey_font_color) || '#ffffff';
    const numSprite = makeJerseyNumberSprite(actor.jersey_number, fontColor);
    group.add(numSprite);
  }

  group.userData.mixer = mixer;
  group.userData.actions = {};
  // Manifest clip cache — clip_id -> THREE.AnimationAction.
  // Populated lazily by ensureClipAction() as verbs are encountered.
  group.userData.manifestActions = {};

  // Register any clips that came embedded in the mesh GLB (Vanguard
  // ships an Idle clip; X-Bot ships none — that's fine). Animation
  // clips are otherwise loaded lazily from per-verb GLBs through the
  // manifest, see ensureClipAction() below.
  Object.entries(clips || {}).forEach(([clipName, clip]) => {
    try {
      group.userData.actions[clipName] = mixer.clipAction(clip);
    } catch (_) { /* clip unusable — skip silently */ }
  });

  // AWAIT the pre-snap stance clip BEFORE returning so the first
  // frame the coach sees is the player IN STANCE, not in T-pose.
  //
  // The Mixamo bind pose for X-Bot is T-pose (arms straight out to the
  // sides). Without this await, createPlayer() resolves the moment the
  // mesh GLB downloads, the actor mounts in T-pose, and the football_stance
  // clip pops in 50–100ms later. That pop was visible in screenshots
  // and read as "players standing aimlessly with arms out" — exactly
  // the symptom coaches reported. Awaiting the clip costs another
  // ~50ms of mount time which is invisible to the coach (the field is
  // already drawn from FieldBuilder), but eliminates the T-pose flash.
  if (roleManifest.pre_snap_stance) {
    try {
      const action = await ensureClipAction(group, roleManifest.pre_snap_stance);
      if (action) {
        // We have to call mixer.update(0) AFTER play() to actually push
        // the clip's first frame into the bones — without it the mesh
        // stays in T-pose until the first render-loop tick (and there
        // can be a frame or two of T-pose before the Three.js render
        // loop actually advances the mixer).
        action.reset();
        action.setLoop(THREE.LoopRepeat, Infinity);
        action.play();
        // applyAliasOpts handles the frozen-frame upright / QB stance
        // for skill / QB roles (Hold/Throw 01 frozen at frame 0). For
        // OL/DL it's a no-op since `football_stance` aliases to a
        // string, not an object. Includes mixer.update(0) internally.
        applyAliasOpts(group, action);
        if (!action._aliasOpts) {
          // Non-frozen path: still advance the mixer once so the first
          // rendered frame is the clip's frame 0, not the bind pose.
          mixer.update(0);
        }
        group.userData.activeAction = action;
        group.userData.activeClipId = roleManifest.pre_snap_stance;
      }
    } catch (err) {
      console.warn('PlayerModel: pre_snap_stance load failed', err && err.message);
    }
  }
  if (roleManifest.default_idle && roleManifest.default_idle !== roleManifest.pre_snap_stance) {
    // Warm the cache so the first stance->idle transition is instant.
    ensureClipAction(group, roleManifest.default_idle).catch(() => {});
  }

  // Labels are sized in world units (1.6 yd tall sprite). Fine when
  // viewed from 80m up in the orthographic top-down camera, but
  // dominate the viewport in QB / sideline / broadcast cameras where
  // we're 1.85m from the player. Tag the label so
  // setLabelsVisibleForCamera() (called by FootballScene3D on camera
  // switch) can hide them in non-top-down views without removing
  // them entirely.
  const label = makeLabelSprite(actor.label || actor.actor_id, '#ffffff');
  label.position.y = 2.45;
  label.userData.isPlayerLabel = true;
  group.add(label);

  // Top-down chalk-talk marker:
  // From the orthographic top-down camera (80yd above the field) the
  // 2yd-tall standing humanoid only presents the top of its head as a
  // single dark pixel. Coaches reported "the field is empty in top
  // view" — accurate. We add a flat ground disc (team-coloured, with
  // jersey number text on top) that lies on the field plane and is
  // visible from above. setLabelsVisibleForCamera() toggles its
  // visibility together with the player labels (top_down only) so it
  // never appears under the player's feet in QB / sideline / end_zone
  // perspectives.
  // NOTE: we do NOT add the top-down marker as a child of the player
  // group anymore. Doing that hit a race / scene-graph issue where
  // children of the SkinnedMesh-rooted player group did not render.
  // Markers are now drawn by FootballScene3D into a sibling overlay
  // group it manages directly (see scene3d._refreshTopDownMarkers).

  return group;
}

/**
 * Public factory used by FootballScene3D to build the top-down chalk-
 * talk marker for a single player. Adds a flat colored disc + facing
 * arrow + jersey-number sprite, all on the field plane (y ≈ 0.05).
 * The caller is responsible for updating the marker's position every
 * frame (or every keyframe) to match the player's world position.
 */
export function makeTopDownPlayerMarker(actor, teamColor) {
  return _makeTopDownMarker(actor, teamColor);
}

/**
 * Resolve the disc/marker color for an actor from its manifest entity
 * visual + uniform spec. Mirrors what createPlayer does internally so
 * the overlay marker matches the actual player jersey.
 */
export function resolvePlayerMarkerColor(actor, entityVisual, uniformSpec = null) {
  const teamColor = parseHex(entityVisual?.color);
  const fallbackHex = `#${teamColor.toString(16).padStart(6, '0')}`;
  const kit = resolveUniformKit(uniformSpec, {
    primary: fallbackHex, helmet: fallbackHex, pads: fallbackHex, accent: fallbackHex,
  });
  return kit.jersey;
}

function _makeTopDownMarker(actor, teamColor) {
  const g = new THREE.Group();
  g.userData.isTopDownMarker = true;
  g.userData.actorIdRef = actor.actor_id;
  // Visible by default; FootballScene3D toggles the parent overlay
  // group's .visible based on active camera, which propagates here.
  g.visible = true;

  // Outer ring: white border so the marker pops against any field
  // colour and team colours of similar value (red defence on green).
  const ringGeo = new THREE.RingGeometry(1.4, 1.7, 32);
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.05;
  ring.renderOrder = 10;
  g.add(ring);

  // Filled disc: team colour. DoubleSide because after rotating the
  // CircleGeometry to lie flat on the field (rotation.x = -PI/2) the
  // default FrontSide normal points DOWN, away from the top-down
  // camera, and the disc would render invisible.
  const discGeo = new THREE.CircleGeometry(1.4, 32);
  const discMat = new THREE.MeshBasicMaterial({
    color: teamColor,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const disc = new THREE.Mesh(discGeo, discMat);
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = 0.06;
  disc.renderOrder = 11;
  g.add(disc);

  // Facing arrow: small triangle on top of the disc pointing in the
  // direction the player faces (so coaches can verify alignment).
  // Players have group.rotation.y set in FootballScene3D so their
  // local +X axis points toward the line of scrimmage. The triangle
  // is drawn in the XZ plane (Y up) pointing in +X so it inherits
  // the parent rotation correctly.
  const triGeo = new THREE.BufferGeometry();
  triGeo.setAttribute('position', new THREE.BufferAttribute(
    new Float32Array([1.1, 0, 0,   0.0, 0, -0.7,   0.0, 0, 0.7]), 3));
  triGeo.setIndex([0, 1, 2]);
  const triMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const tri = new THREE.Mesh(triGeo, triMat);
  tri.position.y = 0.07;
  tri.renderOrder = 12;
  g.add(tri);

  // Jersey number / position label as a sprite ABOVE the disc — uses
  // the existing canvas-sprite trick so it stays readable under
  // orthographic projection (sprite world-size is constant in pixels
  // regardless of camera distance because Sprite.material doesn't
  // honour distance attenuation).
  const labelText = String(actor.jersey_number || actor.label || actor.actor_id || '').slice(0, 4);
  const sprite = _makeChipLabel(labelText);
  sprite.position.set(0, 0.15, 0);
  sprite.renderOrder = 13;
  g.add(sprite);

  return g;
}

function _makeChipLabel(text) {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  ctx.font = 'bold 44px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Drop-shadow outline so number reads on top of any team colour
  ctx.lineWidth = 6;
  ctx.strokeStyle = '#000000';
  ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(2.6, 1.3, 1);
  return sprite;
}

/**
 * Sprint 4 — Pose every player into a position-specific football
 * "ready stance" so the pre-snap diagram looks like a real football
 * formation, not 22 mannequins standing at attention.
 *
 * Bone-pose strategy (Mixamo rig, all archetypes share the same bone
 * names):
 *
 *   - Lineman (3-point):  big forward hip tilt (~40°), counter-spine
 *                         (head still up scanning), hips dropped 10cm
 *                         into the legs, both arms extended forward
 *                         and down (lead hand on the ground).
 *   - QB (under-centre / shotgun): slight athletic crouch, eyes up,
 *                         hands together at chest.
 *   - Receiver / DB (2-point press): athletic stance, hips back, knees
 *                         flexed, arms relaxed at sides.
 *   - Linebacker / safety: slightly more upright 2-point with hands
 *                         ready (arms forward).
 *
 * IMPORTANT: the Mixamo Idle clip animates these same bones every
 * frame from the AnimationMixer, so any one-shot bone tweak we do
 * here gets clobbered ~16ms later. We solve that by:
 *   1. Storing the stance descriptor on the player group
 *      (group.userData.stance = {...})
 *   2. Stopping the Idle clip on linemen so the manual pose persists
 *      (no breathing — but they read as football linemen, which the
 *      Idle pose explicitly does NOT).
 *   3. For non-linemen, leaving the Idle clip running but having
 *      `reapplyStancePose()` overlay our stance on top of the mixer
 *      output every frame (called from updatePlayerMixers).
 *
 * Skipped silently when the GLB doesn't expose Mixamo bones (Tier C
 * fallback discs already look stylised enough).
 */
function _findStanceBones(group) {
  const out = {};
  group.traverse((obj) => {
    if (!obj.isBone) return;
    const name = obj.name || '';
    if (!out.hips && /Hips$/i.test(name)) out.hips = obj;
    if (!out.spine && /(Spine1|Spine)$/i.test(name)) out.spine = obj;
    if (!out.spine2 && /Spine2$/i.test(name)) out.spine2 = obj;
    if (!out.neck && /Neck$/i.test(name)) out.neck = obj;
    if (!out.head && /Head$/i.test(name)) out.head = obj;
    if (!out.leftUpLeg && /LeftUpLeg$/i.test(name)) out.leftUpLeg = obj;
    if (!out.rightUpLeg && /RightUpLeg$/i.test(name)) out.rightUpLeg = obj;
    if (!out.leftLeg && /LeftLeg$/i.test(name)) out.leftLeg = obj;
    if (!out.rightLeg && /RightLeg$/i.test(name)) out.rightLeg = obj;
    if (!out.leftArm && /LeftArm$/i.test(name)) out.leftArm = obj;
    if (!out.rightArm && /RightArm$/i.test(name)) out.rightArm = obj;
    if (!out.leftForeArm && /LeftForeArm$/i.test(name)) out.leftForeArm = obj;
    if (!out.rightForeArm && /RightForeArm$/i.test(name)) out.rightForeArm = obj;
  });
  return out;
}

/**
 * Resolve a position-specific stance descriptor for an actor.
 * The descriptor is in OFFSETS — values to ADD to the mixer's
 * output bone rotations/positions each frame. This way the rest
 * pose from the Idle clip + our offset = the football stance,
 * and breathing motion from the clip still leaks through (very
 * slight body sway) without overpowering the stance silhouette.
 */
function _stanceForRole(role) {
  const r = String(role || '').toLowerCase();
  // CRITICAL: do NOT modify hips.position.y or lowerLegs.rotation.x.
  //   - hips dy moves the entire skeleton vertically (children of hips
  //     include the legs/feet) so dropping hips puts feet THROUGH the
  //     grass; raising them puts the player floating in the air.
  //   - lowerLegs.rx rotates the knee joint forward, lifting the foot
  //     above the ground (since feet are children of lower-leg via
  //     the ankle bone). Result: every player floats.
  // Both of those caused the "players floating in the air or about to
  // fall flat" reports. We now ONLY use:
  //   - hips.rx     forward bend at the waist
  //   - spine.rx    counter-bend so head/eyes stay up
  //   - upperLegs   pulled BACK to compensate for the hip forward bend
  //                 (the upper leg sweeps under the player, not knee-bent)
  //   - arms        forward / down for the stance silhouette
  // Counter-rotating upperLegs with the same magnitude as the hip
  // forward bend keeps the leg world-rotation roughly vertical, so
  // feet stay planted at y=0 regardless of how deep the bend.
  //
  // Defensive backs: shuffle stance, ready to break
  if (/safety|cornerback/.test(r)) {
    return {
      kind: 'db_ready',
      hips: { rx: 0.18 },
      spine: { rx: -0.12 },
      neck:  { rx: -0.05 },
      legs:  { rx: -0.18 },             // counter the hip bend → legs vertical
      arms:  { rx: 0.30 },               // forearms out front
      foreArms: { rx: -0.40 },
      stopIdle: false,
    };
  }
  // Linebackers: more upright, hands ready
  if (/linebacker/.test(r)) {
    return {
      kind: 'lb_ready',
      hips: { rx: 0.16 },
      spine: { rx: -0.10 },
      legs:  { rx: -0.16 },
      arms:  { rx: 0.45 },
      foreArms: { rx: -0.50 },
      stopIdle: false,
    };
  }
  // Linemen: 3-point stance — bent forward, lead hand reaching down.
  // Even though the bend is deep, upperLegs are counter-rotated by the
  // SAME amount as the hip so the legs stay vertical and the feet
  // remain on the ground.
  if (/tackle|guard|center|end$|defensive_line|nose/.test(r)) {
    return {
      kind: '3pt',
      hips: { rx: 0.55 },                // ~31° forward at the waist
      spine: { rx: -0.25 },              // counter-bend; head/eyes up
      spine2: { rx: -0.10 },
      neck: { rx: -0.30 },               // chin up, scanning
      legs: { rx: -0.55 },               // counter-rotate legs back so feet stay planted
      arms: { rx: 0.95, rz_left: 0.20, rz_right: -0.20 },  // arms forward+down
      foreArms: { rx: -0.60 },           // forearms drop (lead-hand stance)
      stopIdle: true,                    // keep stance; idle would stand them up
    };
  }
  // QB (under-centre / shotgun ready)
  if (/quarterback/.test(r)) {
    return {
      kind: 'qb_ready',
      hips: { rx: 0.14 },
      spine: { rx: -0.08 },
      legs:  { rx: -0.14 },
      arms:  { rx: 1.10 },               // hands together at chest
      foreArms: { rx: -1.30 },
      stopIdle: false,
    };
  }
  // Wide receivers / TE / RB: 2-point athletic stance, ready to release
  if (/wide_receiver|tight_end|running_back/.test(r)) {
    return {
      kind: '2pt_release',
      hips: { rx: 0.25 },
      spine: { rx: -0.14 },
      neck: { rx: -0.05 },
      legs:  { rx: -0.25 },
      arms:  { rx: 0.20 },
      foreArms: { rx: -0.20 },
      stopIdle: false,
    };
  }
  // Default: subtle athletic ready stance
  return {
    kind: 'generic_ready',
    hips: { rx: 0.10 },
    spine: { rx: -0.06 },
    legs: { rx: -0.10 },
    arms: null,
    foreArms: null,
    stopIdle: false,
  };
}

export function applyReadyStance(group, actor) {
  const bones = _findStanceBones(group);
  if (!bones.hips) return;
  const stance = _stanceForRole(actor?.role);
  // Cache for re-application every frame (post-mixer-update).
  group.userData.stance = stance;
  group.userData.stanceBones = bones;
  group.userData.stanceMode = 'ready';

  // Linemen: stop the Idle action so the mixer doesn't keep resetting
  // the bones to attention. Their stance pose persists exactly as we
  // set it. Coaches consistently called out linemen looking like
  // soldiers at parade rest — this is the fix.
  if (stance.stopIdle) {
    const actions = group.userData?.actions || {};
    if (actions.Idle) {
      actions.Idle.stop();
      group.userData.activeAction = null;
    }
    if (group.userData.mixer) {
      group.userData.mixer.update(0); // reset to bind pose so we start clean
    }
    _applyStanceOnce(group);
  } else {
    _applyStanceOnce(group);
  }
}

/**
 * Capture the bind-pose rotation/position of every stance bone the
 * first time we touch it. We need this because the Mixamo skeleton's
 * bind pose includes non-zero rotations on hips/spine/etc. to set
 * the upright posture — overwriting those rotations with our stance
 * values produces wildly wrong poses (players ending up prone).
 *
 * Stance offsets are then applied ADDITIVELY on top of the captured
 * bind values every frame.
 */
function _captureBindPose(group, bones) {
  if (group.userData.stanceBindPose) return;
  const bp = {};
  const cap = (b) => b ? {
    rx: b.rotation.x, ry: b.rotation.y, rz: b.rotation.z,
    px: b.position.x, py: b.position.y, pz: b.position.z,
  } : null;
  bp.hips = cap(bones.hips);
  bp.spine = cap(bones.spine);
  bp.spine2 = cap(bones.spine2);
  bp.neck = cap(bones.neck);
  bp.head = cap(bones.head);
  bp.leftUpLeg = cap(bones.leftUpLeg);
  bp.rightUpLeg = cap(bones.rightUpLeg);
  bp.leftLeg = cap(bones.leftLeg);
  bp.rightLeg = cap(bones.rightLeg);
  bp.leftArm = cap(bones.leftArm);
  bp.rightArm = cap(bones.rightArm);
  bp.leftForeArm = cap(bones.leftForeArm);
  bp.rightForeArm = cap(bones.rightForeArm);
  group.userData.stanceBindPose = bp;
}

/** Apply stance offsets ADDITIVELY on top of the captured bind pose. */
function _applyStanceOnce(group) {
  const stance = group.userData?.stance;
  const bones = group.userData?.stanceBones;
  if (!stance || !bones || !bones.hips) return;
  _captureBindPose(group, bones);
  const bp = group.userData.stanceBindPose;

  const setRX = (bone, base, offset) => {
    if (!bone || base == null || offset == null) return;
    bone.rotation.x = base.rx + offset;
  };
  const setRZ = (bone, base, offset) => {
    if (!bone || base == null || offset == null) return;
    bone.rotation.z = base.rz + offset;
  };

  if (stance.hips) {
    setRX(bones.hips, bp.hips, stance.hips.rx);
    // NOTE: stance.hips.dy is intentionally NOT honoured even if a
    // stance descriptor sets it. Modifying hips.position.y moves the
    // entire skeleton (legs are children of hips) and produces feet-
    // through-floor or floating-feet artefacts. Stance descriptors
    // should achieve depth via leg counter-rotation instead.
  }
  if (stance.spine) setRX(bones.spine, bp.spine, stance.spine.rx);
  if (stance.spine2) setRX(bones.spine2, bp.spine2, stance.spine2.rx);
  if (stance.neck) setRX(bones.neck, bp.neck, stance.neck.rx);
  if (stance.legs) {
    setRX(bones.leftUpLeg, bp.leftUpLeg, stance.legs.rx);
    setRX(bones.rightUpLeg, bp.rightUpLeg, stance.legs.rx);
  }
  if (stance.lowerLegs) {
    setRX(bones.leftLeg, bp.leftLeg, stance.lowerLegs.rx);
    setRX(bones.rightLeg, bp.rightLeg, stance.lowerLegs.rx);
  }
  if (stance.arms) {
    setRX(bones.leftArm, bp.leftArm, stance.arms.rx);
    setRX(bones.rightArm, bp.rightArm, stance.arms.rx);
    setRZ(bones.leftArm, bp.leftArm, stance.arms.rz_left);
    setRZ(bones.rightArm, bp.rightArm, stance.arms.rz_right);
  }
  if (stance.foreArms) {
    setRX(bones.leftForeArm, bp.leftForeArm, stance.foreArms.rx);
    setRX(bones.rightForeArm, bp.rightForeArm, stance.foreArms.rx);
  }
}

/**
 * Re-apply the stance pose after the mixer has stepped this frame.
 * Called for every player every frame — fast (just setting bone
 * rotations / positions, no traversal) and the only way the stance
 * stays visible while the Idle clip keeps animating the same bones.
 *
 * Skipped when the player is moving (stanceMode === 'release') so
 * Walk/Run clips can fully drive the body during plays.
 */
export function reapplyStancePose(group) {
  const mode = group.userData?.stanceMode;
  if (mode !== 'ready') return;
  _applyStanceOnce(group);
}

/** Called when a player starts moving — drop stance, let mixer drive. */
export function releaseStance(group) {
  group.userData.stanceMode = 'release';
}

/** Called when a player returns to idle after motion — re-arm stance. */
export function restoreStance(group) {
  if (group.userData?.stance) {
    group.userData.stanceMode = 'ready';
    if (group.userData.stance.stopIdle) {
      const actions = group.userData?.actions || {};
      if (actions.Idle && group.userData.activeAction === actions.Idle) {
        actions.Idle.stop();
      }
    }
    _applyStanceOnce(group);
  }
}

function _fallbackDisc(group, actor, teamColor) {
  const geo = new THREE.CircleGeometry(1.6, 24);
  const mat = new THREE.MeshBasicMaterial({ color: teamColor, side: THREE.DoubleSide });
  const disc = new THREE.Mesh(geo, mat);
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = 0.06;
  group.add(disc);
  const label = makeLabelSprite(actor.label || actor.actor_id, '#ffffff');
  label.position.y = 2.0;
  label.userData.isPlayerLabel = true;
  group.add(label);
  return group;
}

/**
 * Toggle player label sprites based on the active camera. Top-down
 * (orthographic) shows them; perspective cameras (QB / sideline /
 * broadcast / end_zone) hide them — labels at 1.6 yd world height
 * occupy half the viewport from a 1.85m eye point.
 */
export function setLabelsVisibleForCamera(playerGroups, cameraId) {
  const isTopDown = cameraId === 'top_down';
  Object.values(playerGroups || {}).forEach((group) => {
    group.traverse((node) => {
      if (node.userData?.isPlayerLabel) node.visible = isTopDown;
      // Top-down chalk-talk markers (disc + jersey number sprite) are
      // ONLY shown in top_down — they would clip into a player's feet
      // in QB / sideline / end_zone perspectives.
      if (node.userData?.isTopDownMarker) node.visible = isTopDown;
    });
  });
}

/**
 * Cross-fade the player's animation state.
 *
 * speedMps is the current planar velocity in meters/sec (computed from
 * position deltas in FootballScene3D). When `movementType` is supplied,
 * the runtime first tries to resolve a clip by name via
 * `MOVEMENT_TYPE_TO_CLIP` — a backpedaling DB plays "Backpedal" at any
 * speed, not "Walk". Falls back to the speed-based heuristic when no
 * clip matches.
 */
export function setPlayerLocomotion(group, speedMps, movementType = null) {
  const actions = group.userData?.actions;
  if (!actions) return;

  // Stance lifecycle: any motion at all releases the pre-snap stance
  // so the Walk/Run clip drives the body cleanly. When motion stops
  // (locomotion called with speed=0), restore the stance so the
  // player doesn't end the play standing at attention.
  const wasMoving = group.userData.stanceMode === 'release';
  if (speedMps > 0.05 && !wasMoving) {
    releaseStance(group);
  } else if (speedMps <= 0.05 && wasMoving) {
    restoreStance(group);
  }

  let target = null;
  if (movementType) {
    target = actionForMovementType(group, movementType);
  }

  // Skip Idle for stance-locked players (linemen) when stationary —
  // we want the manual 3-point pose, not the Mixamo "stand at
  // attention" idle.
  const stanceLocksIdle = group.userData?.stance?.stopIdle === true
    && group.userData?.stanceMode === 'ready';
  if (!target) {
    if (speedMps > 4.0 && actions.Run) target = actions.Run;
    else if (speedMps > 0.5 && actions.Walk) target = actions.Walk;
    else if (!stanceLocksIdle) target = actions.Idle;
  }

  const current = group.userData.activeAction;
  if (target && target !== current) {
    if (current) current.fadeOut(0.2);
    target.reset().fadeIn(0.2).play();
    group.userData.activeAction = target;
  } else if (!target && current && stanceLocksIdle) {
    current.fadeOut(0.2);
    group.userData.activeAction = null;
  }
}

/**
 * Step all animation mixers attached to player groups.
 * Called once per frame from FootballScene3D.update().
 *
 * Honors the LOD tag set by PlayerLOD.applyLOD():
 *   - userData.mixerFrameSkip === 1 (Tier 0/NEAR): step every frame
 *   - userData.mixerFrameSkip === 2 (Tier 1/MID):  step every other frame
 *   - userData.mixerFrameSkip === 4 (Tier 2/FAR):  step every 4th frame
 *   - userData.mixerFrameSkip === 0 (Tier 3/CULL): never step
 *
 * Multiplies the delta by the skip factor so animations still play at
 * the right wall-clock rate when sampled less often (tradeoff: poses
 * snap by ~1 frame at distance, invisible at >40m).
 */
let _lodFrameCounter = 0;
export function updatePlayerMixers(playerGroups, deltaSeconds) {
  _lodFrameCounter = (_lodFrameCounter + 1) | 0;
  Object.values(playerGroups).forEach((group) => {
    const mixer = group.userData?.mixer;
    if (mixer) {
      const skip = group.userData?.mixerFrameSkip;
      if (skip === undefined) {
        mixer.update(deltaSeconds);
      } else if (skip === 0) {
        // skip
      } else if (skip > 1 && _lodFrameCounter % skip !== 0) {
        // skip
      } else {
        mixer.update(deltaSeconds * skip);
      }
    }
    // Re-apply stance offsets ON TOP of whatever the mixer just
    // produced, every frame. Cheap (sets ~10 bone rotations) and
    // the only way our football pose survives the next Idle frame.
    reapplyStancePose(group);
  });
}

export function disposePlayer(group) {
  group.userData._disposed = true;
  if (group.userData.mixer) {
    group.userData.mixer.stopAllAction();
    group.userData.mixer.uncacheRoot(group);
  }
  group.traverse((obj) => {
    if (obj.geometry && !obj.isSkinnedMesh) {
      obj.geometry.dispose();
    }
    if (obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        if (m.map) m.map.dispose();
        m.dispose();
      });
    }
  });
}
